import { useParams } from 'react-router-dom'

import type { MarketProgram } from '@/features/bidding/constants'
import { BidType } from '@/features/bidding/constants'
import { bidTypeFromSlug, marketProgramFromSlug } from '@/features/bidding/utils/bidDetail/routeSlugs'
import { ENERGY_BIDDING_READ_ENABLED } from '@/features/bidding/utils/getBidConfig'

/**
 * Parses + validates the `:marketProgram` and `:bidType` URL params for the
 * new bid detail route (`/bidding/:marketProgram/:bidType`).
 *
 * Returns `null` if either param is missing or doesn't map to a known enum
 * value. Consumers should redirect to a 404 when this happens.
 */

export type BidDetailRouteParams = Readonly<{
  marketProgram: MarketProgram
  bidType: BidType
}>

export function useBidDetailRouteParams(): BidDetailRouteParams | null {
  const { marketProgram: marketProgramSlug, bidType: bidTypeSlug } = useParams<{
    marketProgram: string
    bidType: string
  }>()

  if (!marketProgramSlug || !bidTypeSlug) {
    return null
  }

  const marketProgram = marketProgramFromSlug(marketProgramSlug)
  const bidType = bidTypeFromSlug(bidTypeSlug)

  if (marketProgram === null || bidType === null) {
    return null
  }

  if (bidType === BidType.ENERGY && !ENERGY_BIDDING_READ_ENABLED.includes(marketProgram)) {
    return null
  }

  return { marketProgram, bidType }
}
